import { useState } from "react";
import { useCustomers } from "@/hooks/use-orders";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { useDebounce } from "@/hooks/use-debounce";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Mail, Phone, MapPin } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { TablePagination } from "@/components/ui/table-pagination";

export function CustomersPage() {
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search);

  const filters: Record<string, string> = { page: String(page), per_page: "20" };
  if (debouncedSearch) filters.search = debouncedSearch;

  const { data, isLoading, error } = useCustomers(storeId, filters);

  if (!storeId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground text-xs">Odaberite trgovinu</p>
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-medium">Kupci</h2>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          className="h-8 text-xs pl-8"
          placeholder="Pretraži po imenu, emailu, telefonu..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="ring-1 ring-foreground/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium">Kupac</th>
                <th className="p-2 text-left font-medium hidden sm:table-cell">Kontakt</th>
                <th className="p-2 text-left font-medium hidden md:table-cell">Lokacija</th>
                <th className="p-2 text-right font-medium">Narudžbe</th>
                <th className="p-2 text-right font-medium hidden sm:table-cell">Potrošeno</th>
                <th className="p-2 text-left font-medium hidden lg:table-cell">Zadnja narudžba</th>
              </tr>
            </thead>
            <tbody>
              {data?.customers?.map((c: any, i: number) => (
                <tr key={c.email || i} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-2">
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {c.first_name} {c.last_name}
                      </span>
                      {c.company && (
                        <span className="text-muted-foreground">{c.company}</span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 hidden sm:table-cell">
                    <div className="flex flex-col gap-0.5 text-muted-foreground">
                      {c.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3 shrink-0" />
                          {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3 shrink-0" />
                          {c.phone}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 hidden md:table-cell">
                    {c.city && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" />
                        {c.city}{c.country ? `, ${c.country}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right font-medium">{c.orders_count}</td>
                  <td className="p-2 text-right hidden sm:table-cell">
                    {c.total_spent} KM
                  </td>
                  <td className="p-2 text-muted-foreground hidden lg:table-cell">
                    {c.last_order ? formatDate(c.last_order) : "—"}
                  </td>
                </tr>
              ))}
              {data?.customers?.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-muted-foreground">
                    Nema kupaca
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <TablePagination page={page} totalPages={data.totalPages} onPageChange={setPage} />}
    </div>
  );
}
